import type { Timestamp } from 'firebase/firestore'

export const OAUTH_INTEGRATION_PROVIDER_IDS = ['G_DRIVE', 'NOTION', 'JIRA', 'GOOGLE_CALENDAR', 'GOOGLE_SHEETS', 'SLACK', 'FIGMA'] as const
export type OAuthIntegrationProviderId = typeof OAUTH_INTEGRATION_PROVIDER_IDS[number]
export const INTEGRATION_PROVIDER_IDS = [...OAUTH_INTEGRATION_PROVIDER_IDS, 'ZOOM', 'DROPBOX', 'GITHUB', 'GENERIC_WEBHOOK', 'STAYS_PMS', 'CLINICS_CONNECTORS'] as const
export type IntegrationProviderId = typeof INTEGRATION_PROVIDER_IDS[number]

export const CONNECTION_STATUSES = ['PENDING', 'ACTIVE', 'CONNECTED', 'DISCONNECTED', 'EXPIRED'] as const
export type ConnectionStatus = typeof CONNECTION_STATUSES[number]
export type IntegrationReadinessCode = 'READY' | 'AUTH_PENDING' | 'AUTH_REQUIRED' | 'TOKEN_INVALID' | 'CHECK_FAILED'

export const INTEGRATION_RUNTIME_STATUSES = ['DISCONNECTED', 'CONNECTING', 'ACTIVE', 'DEGRADED', 'RATE_LIMITED', 'REAUTH_REQUIRED', 'FAILED', 'DISABLED'] as const
export type IntegrationRuntimeStatus = typeof INTEGRATION_RUNTIME_STATUSES[number]

export interface IntegrationRuntimeSummary {
  version: 1
  providerId: string
  connectionId: string
  lifecycleStatus: IntegrationRuntimeStatus
  source: 'CONNECTION_METADATA' | 'STORAGE_CONNECTIONS' | 'UPLOADHUB' | 'SYNC_ENGINE' | 'WEBHOOK' | 'MEETINGS'
  summary: string
  healthSignals: string[]
  aiUsable: boolean
  sync?: {
    state?: string | null
    jobId?: string | null
    trigger?: string | null
    successCount?: number
    failureCount?: number
    conflictCount?: number
    deletionCount?: number
    retryCount?: number
    latencyMs?: number | null
    skippedReason?: string | null
  }
  errors?: {
    code?: string | null
    message?: string | null
    rateLimited?: boolean
    reauthRequired?: boolean
  }
  metrics?: Record<string, number>
  references?: Record<string, string | null>
  updatedAt?: Timestamp
}

export const isOperationalConnectionStatus = (status: ConnectionStatus | undefined): boolean =>
  status === 'ACTIVE' || status === 'CONNECTED'

export interface ConnectionMetadata {
  id: string
  workspaceId: string
  providerId: IntegrationProviderId
  status: ConnectionStatus
  lastSync: Timestamp
  tokenRef: string
  scopes: string[]
  createdAt: Timestamp
  runtime?: IntegrationRuntimeSummary
  runtimeStatus?: IntegrationRuntimeStatus
  runtimeSummary?: string
  runtimeUpdatedAt?: Timestamp
}

export type IntegrationSyncJobStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'SKIPPED'

export interface IntegrationSyncJob {
  id: string
  workspaceId: string
  providerId: 'NOTION' | 'JIRA' | 'GOOGLE_CALENDAR'
  status: IntegrationSyncJobStatus
  trigger?: 'MANUAL' | 'SCHEDULED' | 'PARENT_REPAIR'
  lifecycleStatus?: string
  runtimeStatus?: IntegrationRuntimeStatus
  attemptCount?: number
  successCount: number
  failureCount: number
  conflictCount: number
  deletionCount: number
  summary: { successCount: number; failureCount: number; conflictCount: number; deletionCount: number }
  skippedReason?: string
  errorSummary?: string
  createdAt: Timestamp
  completedAt?: Timestamp
}

export interface LinkHubPaywallEventDetail {
  workspaceId: string
  providerId: IntegrationProviderId
  featureKey?: 'activeIntegrations'
  activeCount?: number
  limit?: number
}

export interface TempOAuthState {
  workspaceId: string
  providerId: IntegrationProviderId
  createdBy: string
  expiresAt: Timestamp
  used: boolean
  returnTo?: string
}

export interface IntegrationInterceptorProps {
  providerLabel: string
  isReady: boolean
  open: boolean
  targetUrl: string
  adminRequired: boolean
  onOpenChange: (open: boolean) => void
}

export interface IntegrationApiKey {
  keyId: string
  workspaceId: string
  name: string
  scopes: string[]
  entities: string[]
  rateLimit: number
  status: 'ACTIVE' | 'REVOKED'
  createdAt: string | null
  lastUsedAt: string | null
}
